import * as React from 'react'
import {Box, Typography, Button, Chip, Tooltip, Collapse, ButtonBase} from '@mui/material'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {sectionCardSx, primaryBtnSx, secondaryBtnSx, kpiLabelSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, MUTED, BORDER, LIGHT_BG, WHITE, PRIMARY_BLUE, SECONDARY_BLUE, SUCCESS, WARNING, ERROR, withOpacity} from '../../../styles/foundation/colors'
import {fmtCurrency, fmtKg, deadline, EXCESS_NOTE} from './format'
import {GROUP_KEYS, SORT_FROM_FILTER, groupRows, orderGroups, triageTotals, groupLevel, isOpen} from './state/triage'
import {declaredFigureSx, billedFigureSx, excessFigureSx, subtleTextSx, governSx, prefixSx, governPrefixSx} from './gridSx'
import EmptyState from './EmptyState'

const GROUP_ICON = {sku: Inventory2OutlinedIcon, skutype: LayersOutlinedIcon, courier: LocalShippingOutlinedIcon}

// The urgency rail down the left edge of every card — the one thing that has
// to survive being scanned at speed, so it is colour only, no text.
const RAIL_COLOR = {crit: ERROR, warn: WARNING, ok: SUCCESS, none: BORDER}
const CLOCK_COLOR = {crit: ERROR, warn: WARNING, ok: SUBTLE, none: MUTED}

// The verdict track. Every card is its own grid, so only FIXED tracks align
// between them — leave this auto and a card reading "Accept (1)" drags the
// money and clock columns sideways relative to one reading "Accept all (4)".
// Measured, not guessed: the widest cluster ("Accept all (n)" + "Dispute all
// (n)") needs 257px.
const ACTIONS_W = 264

// One template, shared by the cards and the header strip above them, so a
// column label can never drift from the column it names.
const GRID_COLS = `28px minmax(170px, 1fr) 88px 124px 108px 120px 124px ${ACTIONS_W}px`

const fmtRange = (r) => (r.varies ? `${r.min.toFixed(2)}–${r.max.toFixed(2)} kg` : fmtKg(r.min))

// The two verdict buttons are laid on an equal-split grid rather than sized by
// their labels. Even with every card reading "Accept all (n)", the digits
// differ in advance width — Poppins ships no tabular figures, so
// font-variant-numeric is a no-op here — and "(1)" came out ~3px narrower than
// "(4)", leaving the pair's left edges wandering across 8px between cards.
// Two 1fr tracks make every button exactly the same width whatever the count,
// and it stays true if a group ever reaches double digits.
const verdictRowSx = {display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, width: '100%'}
const verdictBtnSx = {fontSize: SIZE['2xs'], minWidth: 0}

const labelSx = {...kpiLabelSx, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, mb: 0, whiteSpace: 'nowrap'}
const metaSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * Segmented control, matching FilterBar's View switch so the two read as the
 * same kind of control. Kept local rather than shared because FilterBar's
 * version is a role='tablist' bound to routing-ish view state; this one is a
 * plain radiogroup over a value.
 */
function Segmented({label, value, options, onChange, ariaLabel}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
      {label && <Typography sx={labelSx}>{label}</Typography>}
      <Box role='radiogroup' aria-label={ariaLabel} sx={{display: 'inline-flex', bgcolor: LIGHT_BG, border: `1px solid ${BORDER}`, borderRadius: '8px', p: '2px'}}>
        {options.map((o) => {
          const active = o.id === value
          return (
            <Box
              key={o.id}
              component='button'
              type='button'
              role='radio'
              aria-checked={active}
              onClick={() => onChange(o.id)}
              sx={{
                fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                px: 1.5, py: 0.5, border: 'none', borderRadius: '6px', cursor: 'pointer',
                bgcolor: active ? WHITE : 'transparent',
                color: active ? NAVY : SUBTLE,
                boxShadow: active ? '0 1px 2px rgba(16,24,40,0.08)' : 'none',
                '&:hover': {color: NAVY},
              }}
            >
              {o.label}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

function HeroStat({label, value, sub, color}) {
  return (
    <Box sx={{pl: 2.5, borderLeft: `1px solid ${BORDER}`, minWidth: 132, '&:first-of-type': {pl: 0, borderLeft: 'none'}}}>
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: color || NAVY, lineHeight: 1.35}}>
        {value}
      </Typography>
      <Typography sx={{...metaSx, color: MUTED}}>{sub}</Typography>
    </Box>
  )
}

/**
 * "20 discrepancies → 17 decisions". The whole argument for this view in one
 * line: the queue is not as long as it looks, because the same product
 * packed the same way is re-measured the same way every time.
 */
function Hero({totals, note}) {
  // One line only. The mockup also argued the scale ("≈ 576 decisions across a
  // 1,000-row backlog") and counted the one-offs, but three sentences of
  // reasoning above a queue is text nobody reads on their way to work. The
  // grouping key's own note is the part that earns its place — it says what a
  // group IS — and the one-off count was already redundant with the "One-off"
  // pill on each of those cards.
  return (
    <Box sx={{...sectionCardSx, p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', justifyContent: 'space-between'}}>
      <Box sx={{flex: '1 1 380px', minWidth: 0, maxWidth: 560}}>
        <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap'}}>
          <Typography sx={{fontFamily: FONT, fontSize: '22px', fontWeight: WEIGHT.bold, color: NAVY}}>
            {totals.rows}
          </Typography>
          <Typography sx={{...metaSx, color: SUBTLE}}>
            {totals.rows === 1 ? 'discrepancy' : 'discrepancies'}
          </Typography>
          <ArrowForwardIcon sx={{fontSize: 18, color: MUTED, alignSelf: 'center'}} />
          <Typography sx={{fontFamily: FONT, fontSize: '22px', fontWeight: WEIGHT.bold, color: SECONDARY_BLUE}}>
            {totals.groups}
          </Typography>
          <Typography sx={{...metaSx, color: SUBTLE, fontWeight: WEIGHT.semibold}}>
            {totals.groups === 1 ? 'decision' : 'decisions'}
          </Typography>
          {/* No chip at all on an empty queue — nothing to group is not a
              grouping failure, and "0.0× fewer to read" would read as one. */}
          {totals.rows > 0 && (
            <Chip
              size='small'
              label={totals.reduces ? `${totals.ratio.toFixed(1)}× fewer to read` : 'barely groups — try a coarser key'}
              sx={{
                fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, height: 22,
                bgcolor: withOpacity(totals.reduces ? SUCCESS : WARNING, 0.14),
                color: totals.reduces ? SUCCESS : NAVY,
                // theme/overrides/Chip.js sets textTransform:'capitalize' on
                // every chip label app-wide, which turned this into
                // "1.7x Fewer To Read". Opt out — it is a sentence, not a tag.
                '& .MuiChip-label': {textTransform: 'none'},
              }}
            />
          )}
        </Box>
        <Typography sx={{...metaSx, mt: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
          {note}
        </Typography>
      </Box>

      <Box sx={{display: 'flex', gap: 2.5, flexWrap: 'wrap'}}>
        <Tooltip title={EXCESS_NOTE}>
          <Box>
            <HeroStat
              label='At stake now'
              value={fmtCurrency(totals.stake)}
              sub={`${totals.openN} open in ${totals.needs} ${totals.needs === 1 ? 'group' : 'groups'}`}
            />
          </Box>
        </Tooltip>
        <HeroStat
          label='Soonest auto-accept'
          value={totals.soonLabel || '—'}
          sub={totals.soonTitle || 'nothing on the clock'}
          color={totals.soon ? CLOCK_COLOR[totals.soon.level] : MUTED}
        />
        <HeroStat
          label='Decided here'
          value={`${totals.decided} / ${totals.groups}`}
          sub={totals.decided === 0 ? 'no verdict recorded yet' : `${totals.decided === 1 ? 'group carries' : 'groups carry'} a verdict`}
        />
      </Box>
    </Box>
  )
}

function GroupCard({group, groupBy, expanded, onToggle, onAct, onOpenRow, verdict}) {
  const {stat} = group
  const level = groupLevel(stat)
  const Icon = GROUP_ICON[groupBy] || Inventory2OutlinedIcon
  const many = stat.openN > 1
  // Every card reads "Accept all (n)" / "Dispute all (n)", including n = 1.
  // Dropping "all" on single-shipment groups was grammatically neater but made
  // those buttons narrower than the rest, so their left edges sat inboard of
  // every other card's — the fixed track keeps the RIGHT edges aligned, not the
  // left. One label shape means one button width and one straight column.
  const suffix = ` all (${stat.openN})`
  const openMembers = group.members.filter(isOpen)
  const governs = group.weights.governs
  const deadGoverns = governs === 'HIGHER_DEAD' || governs === 'HIGHER_BOTH'
  const volGoverns = governs === 'HIGHER_VOL' || governs === 'HIGHER_BOTH'

  return (
    <Box sx={{position: 'relative', bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', mb: 1.5, overflow: 'hidden'}}>
      {/* The urgency rail. Faded on a one-off so a single stray shipment
          never shouts as loudly as a repeating pattern. */}
      <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', bgcolor: RAIL_COLOR[level], opacity: stat.n === 1 ? 0.45 : 1}} />

      {/* A fixed-track GRID, not a flex row. Every card is laid out
          independently, so with flex the identity block (the only flexible
          item) absorbed whatever space the other columns left — and those
          differ per card, because "Accept all (2)" is wider than "Accept".
          The result was the clock and money columns landing ~145px apart on
          adjacent cards. Pinning the last three tracks makes all four columns
          line up straight down the list, which is the whole point of a
          scannable queue. Same approach as the mockup's
          `grid-template-columns: 30px minmax(220px,1fr) 136px 132px auto`. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '28px minmax(0, 1fr)', lg: GRID_COLS},
        alignItems: 'center',
        columnGap: 1.5,
        rowGap: 1,
        p: 1.5,
        pl: 2,
      }}>
        <Box
          component='button'
          type='button'
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.title}, ${stat.n} ${stat.n === 1 ? 'shipment' : 'shipments'}${stat.openN ? `, ${fmtCurrency(Math.abs(stat.stake))} at stake` : ', settled'}`}
          sx={{width: 28, height: 28, flexShrink: 0, display: 'grid', placeItems: 'center', border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: MUTED, borderRadius: '6px', '&:hover': {bgcolor: LIGHT_BG, color: NAVY}}}
        >
          <ExpandMoreIcon sx={{fontSize: 20, transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms'}} />
        </Box>

        <Box sx={{minWidth: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
            <Icon sx={{fontSize: 16, color: MUTED, flexShrink: 0}} />
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {group.title}
            </Typography>
            {/* Some rows carry the SKU string as their product name too, and
                printing it as both the title and the chip beside it reads as a
                rendering fault. Same guard the ledger uses for its product
                cell. */}
            {group.code && group.code !== group.title && <Box component='span' sx={codeChipSx}>{group.code}</Box>}
            {group.typeShort && <Box component='span' sx={codeChipSx}>{group.typeShort}</Box>}
          </Box>
          {/* The "3 shipments · 2 Higher Dead · 1 Higher Vol · Shadowfax +1"
              line is gone. The shipment count is already on both buttons and
              in the money sub-line, the courier list is visible the moment the
              group is expanded, and the dead-vs-volumetric split is a
              per-shipment fact that a group average cannot state honestly.
              The weight column now carries what this row is actually about. */}
          <Box sx={{display: 'flex', gap: 0.75, mt: 0.5, flexWrap: 'wrap'}}>
            {stat.n === 1 && <Pill tone='neutral' text='One-off' tip='A single shipment — no repeating pattern. Listed here by value like every other group, never folded into a misc bucket.' />}
            {stat.openN > 0 && (
              <Pill
                tone={stat.evOpen ? 'ok' : 'warn'}
                text={`${stat.evOpen}/${stat.openN} open have evidence`}
                tip={stat.evOpen
                  ? `${stat.evOpen} of ${stat.openN} open shipments already carry evidence.`
                  : `None of the ${stat.openN} open ${stat.openN === 1 ? 'shipment has' : 'shipments have'} evidence yet. One packing video for this group covers all of them.`}
              />
            )}
          </Box>
        </Box>

        {/* Declared / Billed / Excess weight, as three columns in the ledger's
            own shape and styles (gridSx.js) rather than one combined cell —
            same figures, same weights, same order, so a merchant switching
            views is reading the same table. Each renders a range when the
            group's members disagree: under Product SKU the declared figure is
            the SKU's own and holds still while the billed weights scatter,
            which is the pattern itself. */}
        {/* Declared mirrors Billed's shape here too, so the ledger and this
            view describe the same two numbers the same way. Dimensions are
            omitted at group level: a group spans several cartons and one
            averaged box would describe none of them. They are on every
            shipment when the group is expanded. */}
        <Box sx={{minWidth: 0, gridColumn: {xs: '2', lg: 'auto'}}}>
          <Typography sx={{...declaredFigureSx, whiteSpace: 'nowrap'}}>{fmtRange(group.weights.declared)}</Typography>
          <Box sx={{display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 0.75, justifyContent: 'start', alignItems: 'baseline', mt: 0.25}}>
            <Typography sx={prefixSx}>Dead</Typography>
            <Typography sx={{...subtleTextSx, whiteSpace: 'nowrap'}}>{fmtRange(group.weights.declared)}</Typography>
            <Typography sx={prefixSx}>Vol</Typography>
            <Typography sx={{...subtleTextSx, whiteSpace: 'nowrap'}}>{fmtRange(group.weights.declaredVol)}</Typography>
          </Box>
        </Box>

        <Box sx={{minWidth: 0, gridColumn: {xs: '2', lg: 'auto'}}}>
          <Typography sx={{...billedFigureSx, whiteSpace: 'nowrap'}}>{fmtRange(group.weights.billed)}</Typography>
          {/* The dead-vs-volumetric pair on a 2-column grid so both figures
              align regardless of prefix width — copied from the ledger's Billed
              cell. The governing side is highlighted only when every member of
              the group agrees on it; when they do not, neither is, because
              claiming one would be false for some of the shipments. */}
          <Box sx={{display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 0.75, justifyContent: 'start', alignItems: 'baseline', mt: 0.25}}>
            <Typography sx={deadGoverns ? governPrefixSx : prefixSx}>Dead</Typography>
            <Typography sx={{...(deadGoverns ? governSx : subtleTextSx), whiteSpace: 'nowrap'}}>
              {fmtRange(group.weights.dead)}
            </Typography>
            <Typography sx={volGoverns ? governPrefixSx : prefixSx}>Vol</Typography>
            <Typography sx={{...(volGoverns ? governSx : subtleTextSx), whiteSpace: 'nowrap'}}>
              {fmtRange(group.weights.vol)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{minWidth: 0, gridColumn: {xs: '2', lg: 'auto'}}}>
          <Typography sx={excessFigureSx}>+{fmtRange(group.weights.excess)}</Typography>
        </Box>

        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, color: CLOCK_COLOR[level], gridColumn: {xs: '2', lg: 'auto'}}}>
          <AccessTimeIcon sx={{fontSize: 15, opacity: 0.8}} />
          <Box>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: 'inherit', whiteSpace: 'nowrap'}}>
              {stat.soon ? stat.soon.label.replace(/ left$/, '') : '—'}
            </Typography>
            <Typography sx={{...metaSx, color: MUTED}}>
              {stat.soon ? (stat.openN > 1 ? `soonest of ${stat.openN}` : 'until auto-accept') : 'no clock running'}
            </Typography>
          </Box>
        </Box>

        <Tooltip title={EXCESS_NOTE}>
          <Box sx={{textAlign: 'right', minWidth: 0, gridColumn: {xs: '2', lg: 'auto'}}}>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: stat.openN ? NAVY : MUTED}}>
              {fmtCurrency(Math.abs(stat.openN ? stat.stake : stat.totalExcess))}
            </Typography>
            <Typography sx={{...metaSx, color: MUTED}}>
              {stat.openN
                ? `at stake · ${stat.openN} of ${stat.n} open`
                : `settled · ${stat.n} ${stat.n === 1 ? 'shipment' : 'shipments'}`}
            </Typography>
          </Box>
        </Tooltip>

        <Box sx={{display: 'flex', gap: 0.75, alignItems: 'center', justifyContent: 'flex-end', minWidth: 0, gridColumn: {xs: '2', lg: 'auto'}}}>
          {verdict ? (
            // Clickable: it reads as a link and names a set of shipments, so a
            // merchant reasonably expects it to take them there. It was a plain
            // Typography, which is why nothing happened.
            <ButtonBase
              onClick={() => openMembers[0] && onOpenRow(openMembers[0])}
              disabled={!openMembers[0]}
              aria-label={`Open ${(openMembers[0] || {}).awb || 'this group'} to review the ${verdict === 'accepted' ? 'accepted' : 'drafted'} verdict`}
              sx={{
                borderRadius: '6px', px: 0.5,
                '&:focus-visible': {outline: `2px solid ${SECONDARY_BLUE}`, outlineOffset: '2px'},
              }}
            >
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: verdict === 'accepted' ? SUBTLE : SECONDARY_BLUE}}>
                {verdict === 'accepted' ? 'Accepted' : 'Dispute drafted'} · {stat.openN || stat.n} {(stat.openN || stat.n) === 1 ? 'shipment' : 'shipments'}
              </Typography>
            </ButtonBase>
          ) : stat.openN === 0 ? (
            <Box sx={{textAlign: 'right', minWidth: 116}}>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: SUBTLE}}>Settled</Typography>
              <Typography sx={{...metaSx, color: MUTED}}>nothing to decide</Typography>
            </Box>
          ) : (
            <Box sx={verdictRowSx}>
              <Tooltip title={`Confirms the courier's weight on ${stat.openN} open ${stat.openN === 1 ? 'shipment' : 'shipments'} and gives up ${fmtCurrency(Math.abs(stat.stake))}.`}>
                <Button size='small' sx={{...secondaryBtnSx, ...verdictBtnSx}} onClick={() => onAct('accept', group, openMembers)}>
                  {`Accept${suffix}`}
                </Button>
              </Tooltip>
              <Tooltip title={many
                ? `Files one evidence set — a single packing video for this group — against all ${stat.openN} open shipments.`
                : 'Opens the evidence studio for this shipment.'}>
                <Button size='small' sx={{...primaryBtnSx, ...verdictBtnSx, bgcolor: SECONDARY_BLUE, '&:hover': {bgcolor: SECONDARY_BLUE, filter: 'brightness(0.92)'}}} onClick={() => onAct('dispute', group, openMembers)}>
                  {`Dispute${suffix}`}
                </Button>
              </Tooltip>
              {/* No "Review each" button. It called onToggle — exactly what
                  the chevron beside the title already does — so it was a
                  second control for one action, and the widest thing in the
                  row. The chevron keeps the behaviour and its accessible name
                  already says what it opens. */}
            </Box>
          )}
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{borderTop: `1px solid ${BORDER}`, bgcolor: LIGHT_BG, px: 2, py: 1}}>
          {group.members.map((row) => {
            const dl = deadline(row.deadlineAt)
            return (
              <Box key={row.id} sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, flexWrap: 'wrap', borderBottom: `1px solid ${withOpacity(BORDER, 0.5)}`, '&:last-of-type': {borderBottom: 'none'}}}>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY, minWidth: 120}}>{row.awb}</Typography>
                <Typography sx={{...metaSx, minWidth: 90}}>{row.courier}</Typography>
                <Typography sx={{...metaSx, minWidth: 150}}>
                  {fmtKg(row.originalWeight)} → {fmtKg(row.appliedWeight)}
                </Typography>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY, minWidth: 60}}>
                  {fmtCurrency(row.excessAmount)}
                </Typography>
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: CLOCK_COLOR[dl.level] || SUBTLE, minWidth: 90}}>
                  {isOpen(row) ? dl.label : 'Settled'}
                </Typography>
                <Button
                  size='small'
                  onClick={() => onOpenRow(row)}
                  sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, textTransform: 'none', color: PRIMARY_BLUE, ml: 'auto'}}
                >
                  Open
                </Button>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}

const codeChipSx = {
  fontFamily: FONT, fontSize: '11px', color: SUBTLE, bgcolor: LIGHT_BG,
  border: `1px solid ${BORDER}`, borderRadius: '4px', px: 0.5, flexShrink: 0, whiteSpace: 'nowrap',
}

const PILL_TONE = {ok: SUCCESS, warn: WARNING, neutral: MUTED}

function Pill({tone, text, tip}) {
  const color = PILL_TONE[tone] || MUTED
  return (
    <Tooltip title={tip || ''}>
      <Box component='span' sx={{
        display: 'inline-flex', alignItems: 'center', height: 20, px: 1, borderRadius: '999px',
        fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.semibold,
        bgcolor: withOpacity(color, 0.14), color: tone === 'warn' ? NAVY : color,
      }}>
        {text}
      </Box>
    </Tooltip>
  )
}

/**
 * The "By product" view — the same rows the ledger shows, bucketed by cause
 * so one verdict can settle a whole pattern instead of one line at a time.
 */
export default function TriageView({rows, sort, onOpenRow, onGroupAct, onClearFilters}) {
  const [groupBy, setGroupBy] = React.useState('sku')
  const [open, setOpen] = React.useState(() => new Set())
  const [verdicts, setVerdicts] = React.useState(() => new Map())

  // Group order follows FilterBar's sort rather than a control of its own, so
  // the dropdown the merchant can see is never contradicted by the list.
  const groupSort = SORT_FROM_FILTER[sort] || 'money'

  const shown = React.useMemo(
    () => orderGroups(groupRows(rows, groupBy), groupSort),
    [rows, groupBy, groupSort],
  )
  const decidedKeys = React.useMemo(() => new Set(verdicts.keys()), [verdicts])
  const totals = React.useMemo(() => triageTotals(shown, decidedKeys), [shown, decidedKeys])

  const allExpanded = shown.length > 0 && shown.every((g) => open.has(g.key))

  // Changing the grouping key rebuilds every key, so an expansion recorded
  // against the old keys would silently apply to unrelated groups.
  React.useEffect(() => { setOpen(new Set()) }, [groupBy])

  const toggle = (key) => setOpen((s) => {
    const next = new Set(s)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const handleAct = (action, group, members) => {
    setVerdicts((m) => new Map(m).set(group.key, action === 'accept' ? 'accepted' : 'disputed'))
    if (onGroupAct) onGroupAct(action, members)
  }

  const note = (GROUP_KEYS.find((k) => k.id === groupBy) || {}).note

  return (
    <Box>
      <Hero totals={totals} note={note} />

      {/* Group by + Expand all only. The sort segments are gone because
          FilterBar's sort dropdown sits directly above this view and now
          drives the group order (see SORT_FROM_FILTER) — two sort controls on
          one screen, each claiming a different order, is worse than either.
          The "Only groups needing a verdict" switch is gone with it: settled
          groups already read as settled, and hiding them mostly hid work. */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap', mb: 1}}>
        <Segmented label='Group by' ariaLabel='Group by' value={groupBy} options={GROUP_KEYS} onChange={setGroupBy} />
        <Button
          size='small'
          onClick={() => setOpen(allExpanded ? new Set() : new Set(shown.map((g) => g.key)))}
          sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, textTransform: 'none', color: SUBTLE, ml: 'auto'}}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </Box>

      {shown.length === 0 ? (
        <Box sx={{...sectionCardSx, p: 0}}>
          <EmptyState variant='no-match' onClearFilters={onClearFilters} />
        </Box>
      ) : (
        <>
          {/* Column labels. The cards are a table in all but markup, and
              without a header the weight and money figures were unlabelled
              numbers. Shares GRID_COLS with the cards, and is hidden at the
              breakpoint where those columns stack. */}
          <Box sx={{
            display: {xs: 'none', lg: 'grid'},
            gridTemplateColumns: GRID_COLS,
            columnGap: 1.5,
            px: 1.5,
            pl: 2,
            pb: 0.75,
            // The cards carry a 1px border; this strip does not. Without a
            // matching inset its grid resolves 2px wider and every column
            // label drifts off the column it names.
            border: '1px solid transparent',
          }}>
            <Box />
            {/* Product and Deadline cells lead with an icon, so their labels
                are indented by the icon plus its gap to sit over the text
                rather than the glyph. The other two columns start at the
                column edge and need no offset. */}
            <Typography sx={{...labelSx, pl: '24px'}}>Product</Typography>
            <Typography sx={labelSx}>Declared</Typography>
            <Typography sx={labelSx}>Billed</Typography>
            <Typography sx={labelSx}>Excess weight</Typography>
            <Typography sx={{...labelSx, pl: '21px'}}>Deadline</Typography>
            <Typography sx={{...labelSx, textAlign: 'right'}}>Excess freight</Typography>
            <Box />
          </Box>
          {shown.map((g) => (
            <GroupCard
              key={g.key}
              group={g}
              groupBy={groupBy}
              expanded={open.has(g.key)}
              onToggle={() => toggle(g.key)}
              onAct={handleAct}
              onOpenRow={onOpenRow}
              verdict={verdicts.get(g.key)}
            />
          ))}
        </>
      )}
    </Box>
  )
}
